import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fetchPayrollPreview, runPayroll } from "../../store/slices/payrollSlice";
import { payrollService } from "../../services/payrollService";
import Icon from "../../components/Common/Icon";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";
import { useChartColors } from "../../theme/ThemeContext";

const fmtTL = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 2,
  });

const currentMonth = () => new Date().toISOString().slice(0, 7); // YYYY-MM
const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("tr-TR", {
    month: "long",
    year: "numeric",
  });
};

const TABS = [
  { key: "payroll", label: "Bordro", icon: "receipt_long" },
  { key: "salary", label: "Maaşlar", icon: "payments" },
  { key: "advance", label: "Avanslar", icon: "account_balance_wallet" },
  { key: "leave", label: "İzinler", icon: "event_busy" },
];

/** İzin türü etiketleri (backend: LeaveType). */
const LEAVE_TYPES = {
  ANNUAL: { label: "Yıllık İzin", cls: "bg-success/10 text-success" },
  SICK: { label: "Rapor", cls: "bg-tier-gold/10 text-tier-gold" },
  UNPAID: { label: "Ücretsiz", cls: "bg-error/10 text-error" },
  OTHER: { label: "Diğer", cls: "bg-surface-container-high text-on-surface-variant" },
};

const LEAVE_TYPE_OPTIONS = [
  { value: "ANNUAL", label: "Yıllık İzin" },
  { value: "SICK", label: "Rapor" },
  { value: "UNPAID", label: "Ücretsiz" },
  { value: "OTHER", label: "Diğer" },
];

export default function PayrollPage() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { rows, isLoading, error } = useSelector((state) => state.payroll);

  const [tab, setTab] = useState("payroll");
  const [month, setMonth] = useState(currentMonth());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  useEffect(() => {
    dispatch(fetchPayrollPreview(month));
  }, [dispatch, month]);

  const totals = useMemo(
    () =>
      rows.reduce((acc, r) => {
        // Kırılım toplamları yalnız otomatik (RATE) primlerden gelir; sabit ya da
        // elle belirlenen prim hizmet/ürün diye ayrıştırılamaz, sadece genel
        // toplama girer. Bu yüzden hizmet+ürün < toplam prim olabilir.
        const b = commissionBreakdown(r);
        return {
          base: acc.base + Number(r.baseSalary || 0),
          commission: acc.commission + Number(r.commissionTotal || 0),
          serviceCommission: acc.serviceCommission + (b ? b.service : 0),
          productCommission: acc.productCommission + (b ? b.product : 0),
          advance: acc.advance + Number(r.advanceTotal || 0),
          net: acc.net + Number(r.netPay || 0),
        };
      }, { base: 0, commission: 0, serviceCommission: 0, productCommission: 0, advance: 0, net: 0 }),
    [rows]
  );

  const allFinalized = rows.length > 0 && rows.every((r) => r.status === "FINALIZED");

  const doRun = async (finalize) => {
    try {
      await dispatch(runPayroll({ month, finalize })).unwrap();
      showToast(finalize ? "Bordro kesinleştirildi." : "Bordro hesaplandı ve kaydedildi.", "success");
    } catch (err) {
      showToast(typeof err === "string" ? err : "İşlem başarısız.", "error");
    }
  };

  const confirmFinalize = () =>
    setConfirmModal({
      isOpen: true,
      title: "Bordroyu Kesinleştir",
      message: `${monthLabel(month)} bordrosu kesinleştirilecek ve kilitlenecek. Sonrasında değiştirilemez. Emin misiniz?`,
      type: "warning",
      onConfirm: () => doRun(true),
    });

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="payments" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Prim & Maaş</h1>
            <p className="text-on-surface-variant text-sm">
              Personel maaşı, avans ve aylık bordro yönetimi.
            </p>
          </div>
        </div>

        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary"
        />
      </div>

      {/* Sekmeler */}
      <div className="flex items-center gap-1 border-b border-outline-variant">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === t.key
                ? "text-primary border-primary"
                : "text-on-surface-variant border-transparent hover:text-primary"
            }`}
          >
            <Icon name={t.icon} className="!text-[18px]" /> {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {tab === "payroll" && (
        <PayrollTab
          rows={rows}
          isLoading={isLoading}
          totals={totals}
          allFinalized={allFinalized}
          month={month}
          onRun={() => doRun(false)}
          onFinalize={confirmFinalize}
          onRefresh={() => dispatch(fetchPayrollPreview(month))}
          showToast={showToast}
        />
      )}
      {tab === "salary" && <SalaryTab showToast={showToast} />}
      {tab === "advance" && <AdvanceTab month={month} showToast={showToast} />}
      {tab === "leave" && <LeaveTab month={month} showToast={showToast} />}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ Bordro */

const ACCENTS = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success/10 text-success",
  "tier-gold": "bg-tier-gold/10 text-tier-gold",
};

function StatCard({ label, value, icon, accent = "primary", money = true, hint }) {
  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${ACCENTS[accent] ?? ACCENTS.primary}`}>
        <Icon name={icon} className="!text-[20px]" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-on-surface-variant truncate">{label}</p>
        <p className="text-lg font-bold text-on-surface">{money ? fmtTL(value) : value}</p>
        {hint && <p className="text-[11px] text-on-surface-variant truncate">{hint}</p>}
      </div>
    </div>
  );
}

// Primin kaynağını kısa metinle açıklar (bordroda "nereden geldiği" görünür olsun).
const commissionHint = (r) => {
  if (r.commissionOverride != null) return "Elle belirlendi";
  if (r.commissionType === "FIXED") return "Sabit prim";
  if (r.commissionType === "NONE") return "Prim yok";
  if (r.commissionType === "RATE") return "Hizmet + ürün satışı — her kalemin kendi prim oranı";
  return null;
};

/**
 * RATE primin hizmet/ürün kırılımı. Prim elle belirlendiyse veya sabit primse
 * kırılım anlamsızdır; o durumda gösterilmez.
 */
const commissionBreakdown = (r) => {
  if (r.commissionOverride != null || r.commissionType !== "RATE") return null;
  const service = Number(r.commissionServiceTotal ?? 0);
  const product = Number(r.commissionProductTotal ?? 0);
  if (service === 0 && product === 0) return null;
  return { service, product, productBase: Number(r.commissionProductBase ?? 0) };
};

function PayrollTab({ rows, isLoading, totals, allFinalized, month, onRun, onFinalize, onRefresh, showToast }) {
  const C = useChartColors();
  const [editingId, setEditingId] = useState(null);

  // Ödeme durumu özeti: kesinleşmiş (FINALIZED) = ödendi kabul edilir.
  const summary = useMemo(() => {
    const paid = rows.filter((r) => r.status === "FINALIZED");
    const pending = rows.filter((r) => r.status !== "FINALIZED");
    const sum = (arr, k) => arr.reduce((s, r) => s + Number(r[k] || 0), 0);
    return {
      total: rows.length,
      paidCount: paid.length,
      pendingCount: pending.length,
      paidNet: sum(paid, "netPay"),
      pendingNet: sum(pending, "netPay"),
    };
  }, [rows]);

  const statusData = [
    { name: "Ödenen", value: summary.paidCount, color: C.success },
    { name: "Bekleyen", value: summary.pendingCount, color: C.gold },
  ].filter((d) => d.value > 0);

  const breakdownData = [
    { name: "Maaş", value: totals.base, color: C.primary },
    { name: "Prim", value: totals.commission, color: C.success },
    { name: "Avans", value: totals.advance, color: C.gold },
    { name: "Net", value: totals.net, color: C.muted },
  ];

  return (
    <div className="space-y-4">
      {/* Özet kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Maaşı Ödenen Personel"
          value={`${summary.paidCount} / ${summary.total}`}
          money={false}
          icon="task_alt"
          accent="success"
          hint="Bordrosu kesinleşen"
        />
        <StatCard
          label="Bekleyen Personel"
          value={String(summary.pendingCount)}
          money={false}
          icon="pending"
          accent="tier-gold"
          hint="Henüz kesinleşmedi"
        />
        <StatCard label="Bu Ay Ödenen Maaş" value={summary.paidNet} icon="account_balance" accent="primary" hint="Kesinleşen net toplam" />
        <StatCard label="Toplam Avans" value={totals.advance} icon="account_balance_wallet" accent="tier-gold" />
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
          <h3 className="text-sm font-bold text-on-surface mb-1">Ödeme Durumu</h3>
          <p className="text-xs text-on-surface-variant mb-2">Ödenen ve bekleyen personel dağılımı</p>
          {statusData.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-on-surface-variant">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {statusData.map((d) => (
                    <Cell key={d.name} fill={d.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} personel`, n]} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-center gap-4 text-xs">
            {statusData.map((d) => (
              <span key={d.name} className="flex items-center gap-1.5 text-on-surface-variant">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} /> {d.name}: {d.value}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
          <h3 className="text-sm font-bold text-on-surface mb-1">Gider Dağılımı</h3>
          <p className="text-xs text-on-surface-variant mb-2">{monthLabel(month)} toplamları (₺)</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={breakdownData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} width={70}
                     tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
              <Tooltip formatter={(v) => fmtTL(v)} cursor={{ fill: "rgba(124,58,237,0.06)" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {breakdownData.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-on-surface-variant">
          {monthLabel(month)} primi otomatik hesaplanır: tamamlanan randevulardaki her hizmetin kendi prim oranı + personele bağlı her ürün satışının kendi prim oranı. Sabit primli personel bu iki kalemden ayrıca prim almaz.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={onRun}
            disabled={allFinalized}
            className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs disabled:opacity-50 flex items-center gap-1.5"
          >
            <Icon name="calculate" className="!text-[16px]" /> Hesapla & Kaydet
          </button>
          <button
            onClick={onFinalize}
            disabled={rows.length === 0 || allFinalized}
            className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <Icon name="lock" className="!text-[16px]" /> Kesinleştir
          </button>
        </div>
      </div>

      <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-container-low text-on-surface-variant text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Personel</th>
                <th className="text-right px-4 py-3 font-semibold">Maaş</th>
                <th className="text-right px-4 py-3 font-semibold">Hizmet Primi</th>
                <th className="text-right px-4 py-3 font-semibold">Ürün Primi</th>
                <th className="text-right px-4 py-3 font-semibold">Toplam Prim</th>
                <th className="text-right px-4 py-3 font-semibold">Ek/Kesinti</th>
                <th className="text-right px-4 py-3 font-semibold">Avans</th>
                <th className="text-right px-4 py-3 font-semibold">Net</th>
                <th className="text-center px-4 py-3 font-semibold">Durum</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-on-surface-variant">Yükleniyor…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-on-surface-variant">Personel bulunamadı.</td>
                </tr>
              ) : (
                rows.map((r) => {
                  const hint = commissionHint(r);
                  const breakdown = commissionBreakdown(r);
                  return (
                  <Fragment key={r.staffId}>
                  <tr className="hover:bg-surface-container-low/50">
                    <td className="px-4 py-3 font-semibold text-on-surface">{r.staffName}</td>
                    <td className="px-4 py-3 text-right">{fmtTL(r.baseSalary)}</td>
                    {/* Kırılım yalnız otomatik (RATE) primde anlamlı; sabit/elle
                        belirlenen primde kaynak ayrıştırılamaz → "—". */}
                    <td className="px-4 py-3 text-right">
                      {breakdown ? (
                        <span className="text-success">{fmtTL(breakdown.service)}</span>
                      ) : (
                        <span className="text-on-surface-variant">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {breakdown ? (
                        <>
                          <span className="text-success">{fmtTL(breakdown.product)}</span>
                          {breakdown.productBase > 0 && (
                            <div className="text-[11px] text-on-surface-variant" title="Dönemdeki ürün satış cirosu">
                              {fmtTL(breakdown.productBase)} satış
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-on-surface-variant">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-success font-medium">{fmtTL(r.commissionTotal)}</div>
                      {hint && <div className="text-[11px] text-on-surface-variant" title={hint}>{hint}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">{fmtTL(r.adjustmentTotal)}</td>
                    <td className="px-4 py-3 text-right text-tier-gold">−{fmtTL(r.advanceTotal)}</td>
                    <td className="px-4 py-3 text-right font-bold text-on-surface">{fmtTL(r.netPay)}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          r.status === "FINALIZED"
                            ? "bg-primary/15 text-primary border border-primary/20"
                            : "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40"
                        }`}
                      >
                        {r.status === "FINALIZED" ? "Kesinleşti" : r.id ? "Taslak" : "Hesaplanmadı"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.id && r.status !== "FINALIZED" && (
                        <button
                          onClick={() => setEditingId(editingId === r.staffId ? null : r.staffId)}
                          title="Düzenle (prim / ek-kesinti)"
                          className={`p-1.5 rounded-lg transition ${
                            editingId === r.staffId
                              ? "bg-primary/10 text-primary"
                              : "text-primary hover:bg-primary/10"
                          }`}
                        >
                          <Icon name="tune" className="!text-[18px]" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {editingId === r.staffId && (
                    <tr className="bg-surface-container-low/40">
                      <td colSpan={10} className="px-4 py-4">
                        <PayrollRowEditor
                          row={r}
                          onCancel={() => setEditingId(null)}
                          onSaved={() => {
                            setEditingId(null);
                            onRefresh();
                          }}
                          showToast={showToast}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-surface-container-low border-t-2 border-outline-variant">
                <tr className="text-sm">
                  <td className="px-4 py-3 font-bold text-on-surface">Toplam</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtTL(totals.base)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-success">{fmtTL(totals.serviceCommission)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-success">{fmtTL(totals.productCommission)}</td>
                  <td className="px-4 py-3 text-right font-bold text-success">
                    {fmtTL(totals.commission)}
                    {/* Sabit/elle belirlenen primler kırılıma girmez → fark oluşabilir. */}
                    {Math.abs(totals.commission - totals.serviceCommission - totals.productCommission) > 0.01 && (
                      <div
                        className="text-[11px] font-normal text-on-surface-variant"
                        title="Sabit veya elle belirlenen primler hizmet/ürün diye ayrıştırılamaz, yalnız bu toplama girer."
                      >
                        {fmtTL(totals.commission - totals.serviceCommission - totals.productCommission)} ayrıştırılmadı
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-semibold text-tier-gold">−{fmtTL(totals.advance)}</td>
                  <td className="px-4 py-3 text-right font-bold text-on-surface">{fmtTL(totals.net)}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Bordro satırının aynı sayfada (satır içi) düzenlenmesi: prim override,
 * ek/kesinti ve açıklama. Kaydedince PATCH /payroll/{id}/adjustment çağrılır.
 */
function PayrollRowEditor({ row, onCancel, onSaved, showToast }) {
  const [amount, setAmount] = useState(row.adjustmentTotal != null ? String(row.adjustmentTotal) : "0");
  const [note, setNote] = useState(row.adjustmentNote ?? "");
  // Prim override: doluysa o aya özel prim elle belirlenir; boşsa otomatik hesaba döner.
  const [overrideOn, setOverrideOn] = useState(row.commissionOverride != null);
  const [commission, setCommission] = useState(
    row.commissionOverride != null
      ? String(row.commissionOverride)
      : (row.commissionTotal != null ? String(row.commissionTotal) : "0")
  );
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await payrollService.adjust(row.id, {
        adjustmentTotal: Number(amount || 0),
        adjustmentNote: note.trim() || null,
        commissionOverride: overrideOn ? Number(commission || 0) : null,
      });
      showToast("Bordro güncellendi.", "success");
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="text-sm">
      <div className="flex items-center gap-2 mb-3 text-on-surface font-semibold">
        <Icon name="tune" className="text-primary !text-[18px]" /> {row.staffName} — Prim & Ek/Kesinti
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Prim */}
        <div className="rounded-xl border border-outline-variant p-3 bg-surface-card space-y-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-on-surface cursor-pointer">
            <input
              type="checkbox"
              checked={overrideOn}
              onChange={(e) => setOverrideOn(e.target.checked)}
              className="accent-primary"
            />
            Primi bu ay için elle belirle
          </label>
          {overrideOn ? (
            <>
              <input
                type="number"
                step="0.01"
                min="0"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                placeholder="Prim tutarı (₺)"
                className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
              />
              <p className="text-[11px] text-on-surface-variant">
                İşareti kaldırırsanız prim tekrar otomatik hesaplanır.
              </p>
            </>
          ) : (
            <p className="text-[11px] text-on-surface-variant">
              Otomatik: <strong className="text-success">{fmtTL(row.commissionTotal)}</strong>
              {commissionBreakdown(row)
                ? ` · Hizmet ${fmtTL(commissionBreakdown(row).service)} + Ürün ${fmtTL(commissionBreakdown(row).product)}`
                : commissionHint(row) ? ` · ${commissionHint(row)}` : ""}
            </p>
          )}
        </div>

        {/* Ek / Kesinti */}
        <div className="rounded-xl border border-outline-variant p-3 bg-surface-card space-y-2">
          <label className="block text-xs font-semibold text-on-surface-variant">Ek/Kesinti (₺, kesinti için negatif)</label>
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
          />
        </div>

        {/* Açıklama */}
        <div className="rounded-xl border border-outline-variant p-3 bg-surface-card space-y-2">
          <label className="block text-xs font-semibold text-on-surface-variant">Açıklama</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Örn. mesai primi / geç kalma kesintisi"
            className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-3">
        <button type="button" onClick={onCancel} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
          İptal
        </button>
        <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50">
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------- Maaş */

function SalaryTab({ showToast }) {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // staffId
  const [value, setValue] = useState("");
  const [commissionType, setCommissionType] = useState("RATE");
  const [fixedValue, setFixedValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    payrollService
      .listSalaries()
      .then((res) => setSalaries(res.data?.data ?? []))
      .catch(() => showToast("Maaşlar yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (row) => {
    setEditing(row.staffId);
    setValue(row.baseSalary != null ? String(row.baseSalary) : "");
    setCommissionType(row.commissionType ?? "RATE");
    setFixedValue(row.fixedCommission != null ? String(row.fixedCommission) : "");
  };

  const save = async (staffId) => {
    setSaving(true);
    try {
      await payrollService.upsertSalary(staffId, {
        baseSalary: Number(value || 0),
        commissionType,
        fixedCommission: commissionType === "FIXED" ? Number(fixedValue || 0) : 0,
        effectiveFrom: null,
      });
      showToast("Maaş ve prim ayarları güncellendi.", "success");
      setEditing(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-surface-card rounded-2xl p-8 border border-outline-variant text-center text-on-surface-variant">Yükleniyor…</div>;
  }

  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-container-low text-on-surface-variant text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Personel</th>
              <th className="text-right px-4 py-3 font-semibold">Sabit Maaş</th>
              <th className="text-center px-4 py-3 font-semibold">Prim Tipi</th>
              <th className="text-right px-4 py-3 font-semibold">Prim</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant">
            {salaries.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-on-surface-variant">Personel bulunamadı.</td></tr>
            ) : (
              salaries.map((s) => (
                <tr key={s.staffId} className="hover:bg-surface-container-low/50">
                  <td className="px-4 py-3 font-semibold text-on-surface">{s.staffName}</td>
                  <td className="px-4 py-3 text-right">
                    {editing === s.staffId ? (
                      <input
                        type="number"
                        step="0.01"
                        autoFocus
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="w-32 border border-outline-variant rounded-lg px-2 py-1 text-right outline-none focus:border-primary"
                      />
                    ) : (
                      fmtTL(s.baseSalary)
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editing === s.staffId ? (
                      <select
                        value={commissionType}
                        onChange={(e) => setCommissionType(e.target.value)}
                        className="border border-outline-variant rounded-lg px-2 py-1 text-sm bg-white outline-none focus:border-primary"
                      >
                        <option value="RATE">Hizmete Bağlı</option>
                        <option value="FIXED">Sabit Prim (₺)</option>
                        <option value="NONE">Prim Yok</option>
                      </select>
                    ) : (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-surface-container-high text-on-surface-variant">
                        {s.commissionType === "FIXED" ? "Sabit" : s.commissionType === "NONE" ? "Prim Yok" : "Hizmete Bağlı"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-on-surface-variant">
                    {editing === s.staffId ? (
                      commissionType === "FIXED" ? (
                        <div className="inline-flex items-center gap-1 justify-end">
                          <span className="text-xs text-on-surface-variant">₺</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={fixedValue}
                            onChange={(e) => setFixedValue(e.target.value)}
                            className="w-24 border border-outline-variant rounded-lg px-2 py-1 text-right outline-none focus:border-primary"
                          />
                        </div>
                      ) : commissionType === "NONE" ? (
                        <span className="text-[11px] text-on-surface-variant">Prim alınmaz</span>
                      ) : (
                        <span className="text-[11px] text-on-surface-variant">Her hizmetin kendi oranı</span>
                      )
                    ) : (
                      s.commissionType === "FIXED"
                        ? fmtTL(s.fixedCommission)
                        : s.commissionType === "NONE"
                          ? "—"
                          : "Hizmet bazlı"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editing === s.staffId ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => save(s.staffId)} disabled={saving} className="p-1.5 rounded-lg text-success hover:bg-success/10 transition">
                          <Icon name="check" className="!text-[18px]" />
                        </button>
                        <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container-high transition">
                          <Icon name="close" className="!text-[18px]" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(s)} title="Maaş Düzenle" className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition">
                        <Icon name="edit" className="!text-[18px]" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Avans */

function AdvanceTab({ month, showToast }) {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salaries, setSalaries] = useState([]); // personel listesi için
  const [form, setForm] = useState({ staffId: "", amount: "", advanceDate: new Date().toISOString().slice(0, 10), note: "" });
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const load = () => {
    setLoading(true);
    payrollService
      .listAdvances(month)
      .then((res) => setAdvances(res.data?.data ?? []))
      .catch(() => showToast("Avanslar yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    payrollService.listSalaries().then((res) => setSalaries(res.data?.data ?? [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.staffId || !form.amount) {
      showToast("Personel ve tutar zorunludur.", "error");
      return;
    }
    setSaving(true);
    try {
      await payrollService.createAdvance({
        staffId: form.staffId,
        amount: Number(form.amount),
        advanceDate: form.advanceDate,
        periodMonth: month,
        note: form.note.trim() || null,
      });
      showToast("Avans eklendi.", "success");
      setForm({ staffId: "", amount: "", advanceDate: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = (row) =>
    setConfirmModal({
      isOpen: true,
      title: "Avansı Sil",
      message: `${row.staffName} · ${fmtTL(row.amount)} avans kaydı silinecek. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        payrollService
          .deleteAdvance(row.id)
          .then(() => {
            showToast("Avans silindi.", "success");
            load();
          })
          .catch((err) => showToast(err.response?.data?.error ?? "İşlem başarısız.", "error")),
    });

  const total = advances.reduce((s, a) => s + Number(a.amount || 0), 0);

  return (
    <div className="space-y-4">
      {/* Avans ekle */}
      <form onSubmit={submit} className="bg-surface-card rounded-2xl border border-outline-variant p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Personel</label>
          <select
            value={form.staffId}
            onChange={(e) => setForm({ ...form, staffId: e.target.value })}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary"
          >
            <option value="">Seçin…</option>
            {salaries.map((s) => (
              <option key={s.staffId} value={s.staffId}>{s.staffName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Tutar (₺)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Tarih</label>
          <input
            type="date"
            value={form.advanceDate}
            onChange={(e) => setForm({ ...form, advanceDate: e.target.value })}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>
        <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-1.5">
          <Icon name="add" className="!text-[18px]" /> Ekle
        </button>
        <div className="sm:col-span-5">
          <input
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Açıklama (opsiyonel)"
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>
      </form>

      <p className="text-xs text-on-surface-variant">
        {monthLabel(month)} ayına düşecek avanslar · Toplam: <strong className="text-on-surface">{fmtTL(total)}</strong>
      </p>

      <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-container-low text-on-surface-variant text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Personel</th>
                <th className="text-right px-4 py-3 font-semibold">Tutar</th>
                <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold">Açıklama</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-on-surface-variant">Yükleniyor…</td></tr>
              ) : advances.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-on-surface-variant">Bu ay avans kaydı yok.</td></tr>
              ) : (
                advances.map((a) => (
                  <tr key={a.id} className="hover:bg-surface-container-low/50">
                    <td className="px-4 py-3 font-semibold text-on-surface">{a.staffName}</td>
                    <td className="px-4 py-3 text-right text-tier-gold">{fmtTL(a.amount)}</td>
                    <td className="px-4 py-3">{a.advanceDate ? new Date(a.advanceDate).toLocaleDateString("tr-TR") : "—"}</td>
                    <td className="px-4 py-3 text-on-surface-variant">{a.note || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(a)} title="Sil" className="p-1.5 rounded-lg text-error hover:bg-error/10 transition">
                        <Icon name="delete" className="!text-[18px]" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ İzin */

const dayCount = (start, end) => {
  if (!start || !end) return 0;
  const ms = new Date(end) - new Date(start);
  return ms >= 0 ? Math.round(ms / 86400000) + 1 : 0;
};

function LeaveTab({ month, showToast }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salaries, setSalaries] = useState([]); // personel listesi için
  const [form, setForm] = useState({
    staffId: "",
    type: "ANNUAL",
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const load = () => {
    setLoading(true);
    payrollService
      .listLeaves(month)
      .then((res) => setLeaves(res.data?.data ?? []))
      .catch(() => showToast("İzinler yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    payrollService.listSalaries().then((res) => setSalaries(res.data?.data ?? [])).catch(() => {});
  }, []);

  const formDays = dayCount(form.startDate, form.endDate);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.staffId) {
      showToast("Personel seçin.", "error");
      return;
    }
    if (formDays <= 0) {
      showToast("Bitiş tarihi başlangıçtan önce olamaz.", "error");
      return;
    }
    setSaving(true);
    try {
      await payrollService.createLeave({
        staffId: form.staffId,
        type: form.type,
        startDate: form.startDate,
        endDate: form.endDate,
        note: form.note.trim() || null,
      });
      showToast("İzin kaydedildi.", "success");
      setForm({
        staffId: "",
        type: "ANNUAL",
        startDate: new Date().toISOString().slice(0, 10),
        endDate: new Date().toISOString().slice(0, 10),
        note: "",
      });
      load();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = (row) =>
    setConfirmModal({
      isOpen: true,
      title: "İzni Sil",
      message: `${row.staffName} · ${LEAVE_TYPES[row.type]?.label ?? row.type} (${row.dayCount} gün) izin kaydı silinecek. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        payrollService
          .deleteLeave(row.id)
          .then(() => {
            showToast("İzin silindi.", "success");
            load();
          })
          .catch((err) => showToast(err.response?.data?.error ?? "İşlem başarısız.", "error")),
    });

  const totalDays = leaves.reduce((s, l) => s + Number(l.dayCount || 0), 0);

  return (
    <div className="space-y-4">
      {/* İzin ekle */}
      <form onSubmit={submit} className="bg-surface-card rounded-2xl border border-outline-variant p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Personel</label>
          <select
            value={form.staffId}
            onChange={(e) => setForm({ ...form, staffId: e.target.value })}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary"
          >
            <option value="">Seçin…</option>
            {salaries.map((s) => (
              <option key={s.staffId} value={s.staffId}>{s.staffName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-on-surface-variant">İzin Türü</label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary"
          >
            {LEAVE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlangıç</label>
          <input
            type="date"
            value={form.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Bitiş</label>
          <input
            type="date"
            value={form.endDate}
            min={form.startDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>
        <div className="sm:col-span-4">
          <input
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Açıklama (opsiyonel)"
            className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>
        <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-1.5">
          <Icon name="add" className="!text-[18px]" /> {formDays > 0 ? `Ekle (${formDays} gün)` : "Ekle"}
        </button>
      </form>

      <p className="text-xs text-on-surface-variant">
        {monthLabel(month)} ile kesişen izinler · Toplam: <strong className="text-on-surface">{totalDays} gün</strong>
      </p>

      <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-container-low text-on-surface-variant text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Personel</th>
                <th className="text-center px-4 py-3 font-semibold">Tür</th>
                <th className="text-left px-4 py-3 font-semibold">Başlangıç</th>
                <th className="text-left px-4 py-3 font-semibold">Bitiş</th>
                <th className="text-right px-4 py-3 font-semibold">Gün</th>
                <th className="text-left px-4 py-3 font-semibold">Açıklama</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-on-surface-variant">Yükleniyor…</td></tr>
              ) : leaves.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-on-surface-variant">Bu ay izin kaydı yok.</td></tr>
              ) : (
                leaves.map((l) => {
                  const t = LEAVE_TYPES[l.type] ?? LEAVE_TYPES.OTHER;
                  return (
                    <tr key={l.id} className="hover:bg-surface-container-low/50">
                      <td className="px-4 py-3 font-semibold text-on-surface">{l.staffName}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${t.cls}`}>{t.label}</span>
                      </td>
                      <td className="px-4 py-3">{l.startDate ? new Date(l.startDate).toLocaleDateString("tr-TR") : "—"}</td>
                      <td className="px-4 py-3">{l.endDate ? new Date(l.endDate).toLocaleDateString("tr-TR") : "—"}</td>
                      <td className="px-4 py-3 text-right font-medium">{l.dayCount}</td>
                      <td className="px-4 py-3 text-on-surface-variant">{l.note || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => remove(l)} title="Sil" className="p-1.5 rounded-lg text-error hover:bg-error/10 transition">
                          <Icon name="delete" className="!text-[18px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
